/**
 * PATH: packages/bills/src/components/zoomLadder.ts
 * FILE_ID: BILLS-PKG-CMP-ZOOM-001
 * PURPOSE: Pure helpers for the document viewer's zoom ladder.
 *
 * WHY: DocumentViewer was creeping past the 300-line guideline; the
 *      zoom ladder is a pure I→O transform that's independently
 *      testable, so it earns its own module.
 */

/* Step ladder mirroring Acrobat / Chromium's native PDF viewer so
 * users feel at home when they pinch / press +. */
export const ZOOM_STEPS = [50, 67, 80, 100, 125, 150, 200, 300, 400] as const;
export const DEFAULT_ZOOM = 100;
export const MIN_ZOOM = 25;
export const MAX_ZOOM = 500;

/**
 * Move one step up (`dir=1`) or down (`dir=-1`) the ZOOM_STEPS ladder.
 * Falling off either end keeps moving in 25% increments and clamps at
 * MIN/MAX so a fast pinch never sticks at a partial step.
 */
export function nextZoom(current: number, dir: 1 | -1): number {
  if (dir > 0) {
    const next = ZOOM_STEPS.find((z) => z > current);
    return Math.min(MAX_ZOOM, next ?? current + 25);
  }
  const reversed = [...ZOOM_STEPS].reverse();
  const prev = reversed.find((z) => z < current);
  return Math.max(MIN_ZOOM, prev ?? current - 25);
}

export function clampZoom(z: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}
